"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var moment = require("moment");
var React = require("react");
var classNames = require("classnames");
var react_datepicker_1 = require("react-datepicker");
var __1 = require("../../..");
var Messages = require("../../../codegen/Messages");
var useLocale_1 = require("../../../hooks/useLocale");
var InputHelper_1 = require("./InputHelper");
var hooks_1 = require("../../../utils/hooks");
var util_1 = require("../../../utils/util");
var useEscPropagationHandler_1 = require("../../../hooks/useEscPropagationHandler");
require("../../../utils/polyfill");
// See: https://momentjs.com/docs/#/displaying/format/ (Localized formats)
var DatePickerDateFormat;
(function (DatePickerDateFormat) {
    DatePickerDateFormat["ShortMonth_Day_Year"] = "ll";
})(DatePickerDateFormat = exports.DatePickerDateFormat || (exports.DatePickerDateFormat = {}));
// See: https://momentjs.com/docs/#/displaying/format/
var DatePickerTimeFormat;
(function (DatePickerTimeFormat) {
    DatePickerTimeFormat["Hours_Minutes"] = "LT";
    DatePickerTimeFormat["Hours_Minutes_Seconds"] = "LTS";
    DatePickerTimeFormat["UTC_Hours_Minutes"] = "HH:mm [UTC]";
    DatePickerTimeFormat["UTC_Hours_Minutes_Seconds"] = "HH:mm:ss [UTC]";
})(DatePickerTimeFormat = exports.DatePickerTimeFormat || (exports.DatePickerTimeFormat = {}));
var defaultTimeIntervals = 30;
var defaultUtcOffset = 0;
// exported for testing purposes only
var DatePickerInternal = /** @class */ (function (_super) {
    __extends(DatePickerInternal, _super);
    function DatePickerInternal(props) {
        var _this = _super.call(this, props) || this;
        /**
         * Sets the class variable `inputRef` to the datepicker `input` ref on initialization.
         */
        _this.setRef = function (ref) {
            if (ref) {
                var inputRef = _this.props.inputRef;
                _this.ref = ref;
                inputRef && inputRef(ref.input);
            }
        };
        _this.state = { scrollbarsRender: _this.doScrollbarsRender() };
        _this.containerRef = React.createRef();
        return _this;
    }
    /**
     * Feature detect for checking whether or not scrollbars "render" vs "float".
     * e.g. macOS "Show scroll bars" system setting; "Always" vs "When scrolling"
     */
    DatePickerInternal.prototype.doScrollbarsRender = function () {
        var scrollbarsRender = false;
        var overflowElement = document.createElement("div");
        var largerElement = document.createElement("div");
        overflowElement.appendChild(largerElement);
        overflowElement.style.cssText = "border: 0; height: 20px; left: -999em; overflow: scroll; position: absolute; top: -999em; width: 20px;";
        largerElement.style.cssText = "height: 40px; width: 20px;";
        // Put into the page layout to actually calculate
        document.body.appendChild(overflowElement);
        scrollbarsRender =
            overflowElement.offsetWidth - overflowElement.clientWidth > 0;
        // Cleanup
        document.body.removeChild(overflowElement);
        return scrollbarsRender;
    };
    /**
     * Gets the date, time, or date + time format string based on props.
     */
    DatePickerInternal.prototype.getDateFormat = function () {
        var _a = this.props, showTimeSelect = _a.showTimeSelect, showTimeSelectOnly = _a.showTimeSelectOnly, timeFormat = _a.timeFormat, dateFormat = _a.dateFormat;
        if (showTimeSelect) {
            if (showTimeSelectOnly) {
                return timeFormat;
            }
            return dateFormat + " " + timeFormat;
        }
        return dateFormat;
    };
    /**
     * Returns the current UTC offset normalized to minutes.
     */
    DatePickerInternal.prototype.getUtcOffsetInMinutes = function () {
        var utcOffset = this.props.utcOffset;
        if (utcOffset < 16 && utcOffset > -16) {
            return utcOffset * 60;
        }
        return utcOffset;
    };
    DatePickerInternal.prototype.normalizeUtcOffset = function (moment) {
        if (!moment || moment.utcOffset() === this.getUtcOffsetInMinutes()) {
            return moment;
        }
        return moment.utcOffset(this.props.utcOffset);
    };
    /**
     * Gets the extra content to show below the datepicker, such as date limitations,
     * and options for "quick" selection of dates (TODO).
     */
    DatePickerInternal.prototype.getExtraContent = function (onQuickLinkClick) {
        var _a = this.props, infoText = _a.infoText, quickLinks = _a.quickLinks;
        if (infoText || quickLinks) {
            return (React.createElement("div", null,
                React.createElement("div", { className: "react-datepicker__child-container oui-display-inline-block oui-width-1-1 oui-border-top oui-panel" },
                    quickLinks && (React.createElement("div", { className: "oui-float-left oui-child-margin-right" }, quickLinks.map(function (quickLink, index) {
                        var clickFn = function () {
                            onQuickLinkClick(quickLink);
                        };
                        return (React.createElement(__1.Button, { type: __1.ButtonType.Button, buttonStyle: __1.ButtonStyle.MutedLink, onClick: clickFn, key: index }, quickLink.text));
                    }))),
                    infoText && (React.createElement("em", { className: "oui-float-right oui-text-small oui-text-muted" }, infoText)))));
        }
    };
    DatePickerInternal.prototype.render = function () {
        var _this = this;
        var _a = this.props, label = _a.label, labelledBy = _a.labelledBy, disabled = _a.disabled, value = _a.value, defaultValue = _a.defaultValue, fieldName = _a.fieldName, onChange = _a.onChange, onChangeRaw = _a.onChangeRaw, onBlur = _a.onBlur, utcOffset = _a.utcOffset, locale = _a.locale;
        return (React.createElement(InputHelper_1.default, { disabled: disabled, value: value, defaultValue: defaultValue, fieldName: fieldName, onChange: onChange, onBlur: onBlur }, function (helper) {
            var describedBy = [helper.hintId, helper.errorId].join(" ").trim();
            // react-datepicker propagates ESC to parent and closes modals, this handler contains that
            var allowEscPropagation = useEscPropagationHandler_1.default(_this.containerRef, 
            // since datepicker opens on focus we need to default to blocking propagation
            // @todo - when upgrading to v2.x react-datepicker, leverage onCalendarOpen and onCalendarClose instead
            true).allowEscPropagation;
            // Clicking on a date item only fires an onchange, but some text input only fires onChangeRaw
            // In order to capture everything (and to set the form date to undefined when there is a bad
            // date string entered) we want all events to flow through the mappedOnChangeRaw handler
            var mappedOnChange = function (moment, event) {
                allowEscPropagation();
                // Triggering a raw update ends up calling the onChange again, we used a custom event with a marker
                // in the detail to make sure that we don't end up triggering additional events after the first
                if (event) {
                    var maybeDetail = event.nativeEvent.detail;
                    if (maybeDetail && maybeDetail._generated) {
                        return;
                    }
                }
                // There's a bug in the Calendar where two days can be highlighted because for some reason
                // the moments that get created inside of react-datepicker (or maybe the calendar) are created
                // non normalized.  We'll normalize it here, but before we do, get the string version of the non-normalized
                // moment to trigger an event with.
                var dateStr = moment && moment.format(_this.getDateFormat());
                _this.normalizeUtcOffset(moment);
                // Use a custom event with a marker we can examine to prevent firing multiple events per change
                var synthEvent = new CustomEvent("input", {
                    bubbles: true,
                    cancelable: true,
                    detail: {
                        _generated: true
                    }
                });
                Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value").set.call(_this.ref.input, dateStr);
                _this.ref.input.dispatchEvent(synthEvent);
            };
            var mappedOnChangeRaw = function (event) {
                allowEscPropagation();
                var parsedDate = moment(event.currentTarget.value, _this.getDateFormat(), locale, true).utcOffset(utcOffset, true);
                helper.onChange(parsedDate.isValid() ? parsedDate.toDate() : undefined);
                onChangeRaw && onChangeRaw(event);
            };
            // If the utcOffset value changes, trigger an update
            var lastUtc = hooks_1.usePrevious(utcOffset, utcOffset);
            React.useEffect(function () {
                if (utcOffset !== lastUtc && helper.value) {
                    mappedOnChange(moment(helper.value).utcOffset(utcOffset));
                }
            }, [utcOffset]);
            var onQuickLinkClick = function (quickLink) {
                // Ensure the datepicker is closed; if it isn't, re-renders *may* end up throwing errors
                _this.ref.setOpen(false);
                if (quickLink.onClick) {
                    quickLink.onClick();
                }
                else {
                    mappedOnChange(moment(quickLink.date).utcOffset(utcOffset));
                }
            };
            var _a = _this.props, autoFocus = _a.autoFocus, disabled = _a.disabled, endDate = _a.endDate, excludeDates = _a.excludeDates, excludeTimes = _a.excludeTimes, includeDates = _a.includeDates, includeTimes = _a.includeTimes, maxDate = _a.maxDate, maxTime = _a.maxTime, minDate = _a.minDate, minTime = _a.minTime, monthsShown = _a.monthsShown, openToDate = _a.openToDate, placeholderText = _a.placeholderText, selectsEnd = _a.selectsEnd, selectsStart = _a.selectsStart, showTimeSelect = _a.showTimeSelect, showTimeSelectOnly = _a.showTimeSelectOnly, startDate = _a.startDate, startOpen = _a.startOpen, tabIndex = _a.tabIndex, timeCaption = _a.timeCaption, timeFormat = _a.timeFormat, timeIntervals = _a.timeIntervals, testId = _a.testId;
            var scrollbarsRender = _this.state.scrollbarsRender;
            var datePickerProps = {
                autoFocus: autoFocus,
                disabled: disabled,
                locale: locale,
                monthsShown: monthsShown,
                placeholderText: placeholderText,
                selectsEnd: selectsEnd,
                selectsStart: selectsStart,
                showTimeSelect: showTimeSelect,
                showTimeSelectOnly: showTimeSelectOnly,
                startOpen: startOpen,
                tabIndex: tabIndex,
                timeFormat: timeFormat,
                timeIntervals: timeIntervals,
                onBlur: helper.onBlur,
                calendarClassName: classNames({
                    "react-datepicker__floating-scrollbars": !scrollbarsRender
                }, {
                    "react-datepicker__full_width": showTimeSelect && !showTimeSelectOnly
                }),
                dateFormat: _this.getDateFormat(),
                endDate: endDate && moment(endDate),
                excludeDates: excludeDates && excludeDates.map(function (ed) { return moment(ed); }),
                excludeTimes: excludeTimes && excludeTimes.map(function (et) { return moment(et); }),
                id: helper.fieldId,
                includeDates: includeDates && includeDates.map(function (id) { return moment(id); }),
                includeTimes: includeTimes && includeTimes.map(function (it) { return moment(it); }),
                maxDate: maxDate && moment(maxDate),
                maxTime: maxTime && moment(maxTime),
                minDate: minDate && moment(minDate),
                minTime: minTime && moment(minTime),
                name: helper.fieldName,
                onChange: mappedOnChange,
                // TODO: react-datepicker type definitions are wrong; the event passed to this callback
                // is `React.ChangeEvent<HTMLInputElement>` but is defined as `React.FocusEvent<HTMLInputElement>`
                onChangeRaw: mappedOnChangeRaw,
                openToDate: openToDate && moment(openToDate),
                selected: helper.value && moment(helper.value).utcOffset(utcOffset),
                startDate: startDate && moment(startDate),
                timeCaption: timeCaption || Messages.DatePicker.time(),
                utcOffset: utcOffset || 0
            };
            var showIcon = !(showTimeSelect && showTimeSelectOnly);
            return (React.createElement("div", __assign({ className: "oui-position-relative", "data-test-id": testId }, util_1.ariaProps({ label: label, labelledBy: labelledBy, describedBy: describedBy }), { ref: _this.containerRef }),
                React.createElement(react_datepicker_1.default, __assign({}, datePickerProps, { autoComplete: "off", className: "oui-input react-datepicker__input", ref: _this.setRef, showDisabledMonthNavigation: true }), _this.getExtraContent(onQuickLinkClick)),
                showIcon && (React.createElement("span", { className: "oui-position-center-right oui-margin-small-right oui-datepicker__icon", "data-oui-icon": "calendar" }))));
        }));
    };
    DatePickerInternal.defaultProps = {
        dateFormat: DatePickerDateFormat.ShortMonth_Day_Year,
        timeFormat: DatePickerTimeFormat.UTC_Hours_Minutes,
        timeIntervals: defaultTimeIntervals,
        utcOffset: defaultUtcOffset
    };
    return DatePickerInternal;
}(React.Component));
exports.DatePickerInternal = DatePickerInternal;
/**
 * OUI datepicker component.
 * A datepicker should be used when a user needs to pick a date, time, or date & time combination.
 *
 * @oui-component DatePicker
 */
var DatePicker = function (props) {
    var locale = useLocale_1.default();
    // workaround - initialize moment locale globally
    // as react-datepicker 1.x does not use it's own locale prop when rendering local "times"
    moment.locale(locale);
    return React.createElement(DatePickerInternal, __assign({ locale: locale }, props));
};
exports.default = DatePicker;
//# sourceMappingURL=DatePicker.js.map